import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Globe, Check, Copy, RefreshCw, Shield, ExternalLink, 
  AlertCircle, CheckCircle2, Settings, Info, HelpCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// DNS Setup Instructions for Common Registrars
const DNS_REGISTRARS = {
  namecheap: {
    name: "Namecheap",
    steps: [
      "Log in to your Namecheap account",
      "Go to Domain List → click 'Manage' next to your domain",
      "Click on 'Advanced DNS' tab",
      "Click 'Add New Record'",
      "For CNAME: Type='CNAME Record', Host='www', Value='microsites.effyrealty.com'",
      "For TXT: Type='TXT Record', Host='_effy-verify', Value='[your-token]'",
      "Click the checkmark to save"
    ]
  },
  godaddy: {
    name: "GoDaddy",
    steps: [
      "Log in to your GoDaddy account",
      "Go to 'My Products' → click 'DNS' next to your domain",
      "Click 'Add' in the Records section",
      "For CNAME: Type='CNAME', Name='www', Value='microsites.effyrealty.com'",
      "For TXT: Type='TXT', Name='_effy-verify', Value='[your-token]'",
      "Click 'Save'"
    ]
  },
  cloudflare: {
    name: "Cloudflare",
    steps: [
      "Log in to Cloudflare dashboard",
      "Select your domain",
      "Go to 'DNS' section",
      "Click 'Add record'",
      "For CNAME: Type='CNAME', Name='www', Target='microsites.effyrealty.com', Proxy=OFF",
      "For TXT: Type='TXT', Name='_effy-verify', Content='[your-token]'",
      "Click 'Save'"
    ]
  },
  route53: {
    name: "AWS Route 53",
    steps: [
      "Log in to AWS Console → Route 53",
      "Select your hosted zone",
      "Click 'Create record'",
      "For CNAME: Type='CNAME', Name='www', Value='microsites.effyrealty.com'",
      "For TXT: Type='TXT', Name='_effy-verify', Value='\"[your-token]\"' (with quotes)",
      "Click 'Create records'"
    ]
  },
  hostinger: {
    name: "Hostinger",
    steps: [
      "Log in to Hostinger hPanel",
      "Go to Domains → select your domain",
      "Click 'DNS / Nameservers'",
      "Under 'DNS Records', click 'Add Record'",
      "For CNAME: Type='CNAME', Name='www', Points to='microsites.effyrealty.com'",
      "For TXT: Type='TXT', Name='_effy-verify', TXT Value='[your-token]'",
      "Click 'Add Record'"
    ]
  }
};

const DomainSettingsPage = () => {
  const { builder } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [domainSettings, setDomainSettings] = useState(null);
  const [customDomain, setCustomDomain] = useState('');
  const [allowPartnerDomains, setAllowPartnerDomains] = useState(true);
  const [dnsInstructions, setDnsInstructions] = useState(null);
  const [selectedRegistrar, setSelectedRegistrar] = useState('namecheap');
  const [verificationResult, setVerificationResult] = useState(null);

  useEffect(() => {
    if (builder?.id) {
      loadDomainSettings();
    }
  }, [builder]);

  const loadDomainSettings = async () => {
    try {
      const res = await axios.get(`${API}/builders/${builder.id}/domain-settings`);
      setDomainSettings(res.data);
      setCustomDomain(res.data.domain_settings?.custom_domain || '');
      setAllowPartnerDomains(res.data.domain_settings?.allow_partner_domains ?? true);
    } catch (error) {
      toast.error('Failed to load domain settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDomain = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/builders/${builder.id}/domain-settings`,
        null,
        { params: { custom_domain: customDomain || null, allow_partner_domains: allowPartnerDomains } }
      );
      toast.success('Domain settings saved');
      setDnsInstructions(res.data.dns_instructions);
      loadDomainSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save domain settings');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    setVerifying(true);
    setVerificationResult(null);
    try {
      const res = await axios.post(`${API}/builders/${builder.id}/verify-domain`);
      setVerificationResult(res.data);
      
      if (res.data.verification_details?.fully_verified) {
        toast.success('Domain verified successfully!');
        loadDomainSettings();
        setDnsInstructions(null);
      } else {
        toast.error('DNS records not found yet. Please check the configuration and try again.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Domain verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleRemoveDomain = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/builders/${builder.id}/domain-settings`,
        null,
        { params: { custom_domain: '' } }
      );
      toast.success('Custom domain removed');
      setCustomDomain('');
      setDnsInstructions(null);
      loadDomainSettings();
    } catch (error) {
      toast.error('Failed to remove domain');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (err) {
      // Fallback for when clipboard API is blocked (e.g., in iframes)
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Copied to clipboard');
      } catch (e) {
        toast.error('Copy failed - please copy manually');
      }
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  const isVerified = domainSettings?.domain_settings?.is_verified;
  const currentDomain = domainSettings?.domain_settings?.custom_domain;

  return (
    <div className="space-y-6" data-testid="domain-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Globe className="h-7 w-7 text-blue-600" />
          Domain Settings
        </h1>
        <p className="text-muted-foreground">
          Configure custom domains for your microsites
        </p>
      </div>

      {/* Current Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Current Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Default URL Pattern</p>
              <p className="font-mono text-sm">
                effyrealty.com/m/<span className="text-blue-600">{'{slug}'}</span>
              </p>
            </div>
            
            {currentDomain && (
              <div className={`p-4 rounded-lg ${isVerified ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm text-muted-foreground">Custom Domain</p>
                  <Badge variant={isVerified ? 'default' : 'secondary'} className={isVerified ? 'bg-emerald-600' : ''}>
                    {isVerified ? <><CheckCircle2 className="h-3 w-3 mr-1" /> Verified</> : <><AlertCircle className="h-3 w-3 mr-1" /> Pending</>}
                  </Badge>
                </div>
                <p className="font-mono text-sm">
                  {currentDomain}/<span className="text-blue-600">{'{slug}'}</span>
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Custom Domain Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Custom Domain</CardTitle>
          <CardDescription>
            Add your own domain for professional microsite URLs (e.g., microsites.yourcompany.com)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Label htmlFor="custom-domain">Domain</Label>
              <Input
                id="custom-domain"
                placeholder="microsites.yourcompany.com"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleSaveDomain} disabled={saving}>
                {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Save
              </Button>
              {currentDomain && (
                <Button variant="outline" onClick={handleRemoveDomain} disabled={saving}>
                  Remove
                </Button>
              )}
            </div>
          </div>

          {/* DNS Instructions */}
          {(dnsInstructions || (currentDomain && !isVerified)) && (
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription>
                <p className="font-semibold text-blue-800 mb-3">DNS Configuration Required</p>
                <p className="text-sm text-blue-700 mb-4">
                  Add the following records to your domain's DNS settings:
                </p>
                
                <div className="space-y-3">
                  {/* CNAME Record */}
                  <div className="bg-white p-3 rounded border">
                    <div className="flex items-center justify-between mb-2">
                      <Badge>CNAME Record</Badge>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => copyToClipboard('microsites.effyrealty.com')}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm font-mono">
                      <div>
                        <span className="text-muted-foreground">Name:</span> {dnsInstructions?.name || customDomain?.split('.')[0] || '@'}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Value:</span> microsites.effyrealty.com
                      </div>
                    </div>
                  </div>

                  {/* TXT Record */}
                  <div className="bg-white p-3 rounded border">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary">TXT Record (Verification)</Badge>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => copyToClipboard(domainSettings?.domain_settings?.verification_token || '')}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm font-mono">
                      <div>
                        <span className="text-muted-foreground">Name:</span> _effy-verify
                      </div>
                      <div className="break-all">
                        <span className="text-muted-foreground">Value:</span> {domainSettings?.domain_settings?.verification_token}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Registrar-specific Instructions */}
                <div className="mt-4 p-3 bg-white rounded border">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="flex items-center gap-2">
                      <HelpCircle className="h-4 w-4" />
                      Step-by-step guide for your registrar
                    </Label>
                    <Select value={selectedRegistrar} onValueChange={setSelectedRegistrar}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(DNS_REGISTRARS).map(([key, reg]) => (
                          <SelectItem key={key} value={key}>{reg.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                    {DNS_REGISTRARS[selectedRegistrar]?.steps.map((step, i) => (
                      <li key={i} className={step.includes('[your-token]') ? 'font-medium text-blue-700' : ''}>
                        {step.replace('[your-token]', domainSettings?.domain_settings?.verification_token || '[your-token]')}
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Verification Result */}
                {verificationResult && !verificationResult.verification_details?.fully_verified && (
                  <Alert className="mt-3 bg-amber-50 border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      <p className="font-semibold">Verification Status</p>
                      <div className="text-sm mt-2 space-y-1">
                        <p className="flex items-center gap-2">
                          {verificationResult.verification_details?.txt_record?.verified ? 
                            <CheckCircle2 className="h-4 w-4 text-green-600" /> : 
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          }
                          TXT Record: {verificationResult.verification_details?.txt_record?.message}
                        </p>
                        <p className="flex items-center gap-2">
                          {verificationResult.verification_details?.cname_record?.verified ? 
                            <CheckCircle2 className="h-4 w-4 text-green-600" /> : 
                            <AlertCircle className="h-4 w-4 text-amber-500" />
                          }
                          CNAME Record: {verificationResult.verification_details?.cname_record?.message}
                        </p>
                      </div>
                      <p className="text-xs mt-2">DNS changes can take 5 minutes to 48 hours to propagate.</p>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="mt-4 flex gap-2">
                  <Button onClick={handleVerifyDomain} disabled={verifying}>
                    {verifying ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    Verify Domain
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Verified Success */}
          {currentDomain && isVerified && (
            <Alert className="bg-emerald-50 border-emerald-200">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-emerald-800">
                <p className="font-semibold">Domain Verified & Active</p>
                <p className="text-sm mt-1">
                  Your microsites are now accessible at <span className="font-mono">{currentDomain}/{'{slug}'}</span>
                </p>
                {domainSettings?.domain_settings?.ssl_enabled && (
                  <p className="text-sm mt-1 flex items-center gap-1">
                    <Shield className="h-3 w-3" /> SSL certificate is active
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Partner Domain Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Partner Domain Settings</CardTitle>
          <CardDescription>
            Control whether channel partners can use their own custom domains for microsites
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium">Allow Partner Custom Domains</p>
              <p className="text-sm text-muted-foreground">
                Partners can configure their own domains (e.g., www.partnername.com)
              </p>
            </div>
            <Switch
              checked={allowPartnerDomains}
              onCheckedChange={(checked) => {
                setAllowPartnerDomains(checked);
                // Auto-save this setting
                axios.put(
                  `${API}/builders/${builder.id}/domain-settings`,
                  null,
                  { params: { allow_partner_domains: checked } }
                ).then(() => toast.success('Setting updated'));
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DomainSettingsPage;
